import { Injectable } from '@angular/core'
import { UserStorage } from '@tv3/services/utils/storage.service'
import { InfoDialogService } from '@tokeet/tv3-platform'
import { PlanService } from '@tv3/store/plan/plan.service'
import { of } from 'rxjs'
import { filter, switchMap, switchMapTo } from 'rxjs/operators'
import { NgxPermissionsService } from 'ngx-permissions'

export const YEARLY_PLAN_SUNSET = 'YEARLY_PLAN_SUNSET'
export const AIRBNB_VERSION_1_SUNSET = 'AIRBNB_VERSION_1_SUNSET'
export const AIRBNB_ICAL_CHANGE = 'AIRBNB_ICAL_CHANGE'
export const PRICING_CHANGE = 'PRICING_CHANGE_V2'

@Injectable({
  providedIn: 'root',
})
export class AnnouncementService {
  constructor(
    private storage: UserStorage,
    private planService: PlanService,
    private ngxPermissionsService: NgxPermissionsService,
    private infoDialog: InfoDialogService
  ) {}

  announceYearlyPlanSunset() {
    of(this.storage.get(YEARLY_PLAN_SUNSET))
      .pipe(
        filter(
          (isAlreadyAnnounced) =>
            !isAlreadyAnnounced && !!this.ngxPermissionsService.getPermission('canSeeYearlyPlanAnnouncement')
        ),
        switchMapTo(this.planService.status()),
        filter((status) => {
          const isYearly = (status.planId + '').includes('_yr') // better be safe, make it a string
          this.storage.set(YEARLY_PLAN_SUNSET, true)
          return isYearly
        }),
        switchMap((status) => {
          return this.infoDialog.open({
            title: 'Your Subscription Will Change Soon',
            body: `
              As of September, 2018, Tokeet stopped offering new yearly plans to our customers. Users who were already on a yearly plan were allowed to continue on their plan for a 9 month grace period.
              <br><br>
              As of June 25th, 2019 all customers currently on a yearly plan will be automatically transitioned to the equivalent monthly plan.
              <br><br>
              If you are currently on a yearly plan, you may optionally convert to a monthly plan yourself at anytime by logging in to the Tokeet application and going to the "My Billing Info" page. Your account will be given a prorated credit for the amount of time left on your annual plan once this change takes effect. If you have any questions please do not hesitate to contact customer support.
            `,
          })
        })
      )
      .subscribe()
  }

  announceAirbnbVersion1Sunset() {
    of(this.storage.get(AIRBNB_VERSION_1_SUNSET))
      .pipe(
        filter(
          (isAlreadyAnnounced) =>
            !isAlreadyAnnounced && !!this.ngxPermissionsService.getPermission('canSeeAirbnbVersion1Announcement')
        ),
        switchMap(() => {
          this.storage.set(AIRBNB_VERSION_1_SUNSET, true)
          return this.infoDialog.open({
            title: 'Airbnb Version 1 API',
            body: `
              As of September 15th Airbnb will no longer support connections made via the old Airbnb API.
              <br><br>
              Tokeet will begin the process of removing any connections made via this API on September 15th.
              <br><br>
              In order to maintain a proper connection with Airbnb it is vital that you begin the migration process to the new official version.
            `,
          })
        })
      )
      .subscribe()
  }

  announceAirbnbIcalChange() {
    of(this.storage.get(AIRBNB_ICAL_CHANGE))
      .pipe(
        filter(
          (isAlreadyAnnounced) =>
            !isAlreadyAnnounced && !!this.ngxPermissionsService.getPermission('canSeeAirbnbICalAnnouncement')
        ),
        switchMap(() => {
          this.storage.set(AIRBNB_ICAL_CHANGE, true)
          return this.infoDialog.open({
            title: 'Airbnb iCal Announcement',
            body: `
              Due to GDPR and other privacy regulations, Airbnb will stop sending Guest details in their iCal feeds as of December 1st, 2019.
              <br><br>
              As a result, Tokeet will no longer be able to create bookings from Airbnb iCal URLs.
              <br><br>
              Airbnb iCal feeds will create hold events in Tokeet instead of bookings as of December 1st, 2019.
            `,
          })
        })
      )
      .subscribe()
  }

  announcePricingChange() {
    of(this.storage.get(PRICING_CHANGE))
      .pipe(
        filter(
          (isAlreadyAnnounced) =>
            !isAlreadyAnnounced && !!this.ngxPermissionsService.getPermission('canSeePricingAnnouncement')
        ),
        switchMap(() => {
          this.storage.set(PRICING_CHANGE, true)
          return this.infoDialog.open({
            title: 'Tokeet Announcement',
            body: `
              Our prices are changing on March 1st.
              <br><br>
              You can keep your currently plan and get a 10% discount if you wish.
              <br><br>
              Please <a target="_blank" href="https://www.tokeet.com/blog/upcoming-changes-for-2020/">click here</a> for more details.
            `,
          })
        })
      )
      .subscribe()
  }
}
